import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserContext } from '../../contexts/UserContext';
import checkUserPermissions from '../checkUserPermissions';
import { openSnackbar } from '../../api/snackbar';
import Loader from '../../components/Loader';

/**
 * Role hierarchy: superadmin > admin > staff
 *
 * requiredRole (prop):
 *   'staff'  – any venue member can access (admin + staff).
 *   'admin'  – only venue admins (or super admins).
 *
 * If the user has no venue access at all (role === null) they are logged out.
 * If the user has access but insufficient role they are redirected back with a snackbar.
 */
const ROLE_LEVEL = { staff: 0, admin: 1, superadmin: 2 };

export default function VenueGuard({ children, requiredRole = 'staff' }) {
  const { user, selectedVenueData, venueDataLoading, logout } = useUserContext();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  const role = user && selectedVenueData ? checkUserPermissions(user, selectedVenueData) : undefined;

  useEffect(() => {
    // Still loading — don't make any decisions yet
    if (!user || venueDataLoading || !selectedVenueData) return;
    if (hasRedirected.current) return;

    if (role === undefined || role === null) {
      return;
    }

    // No venue access → log out
    if (!role) {
      hasRedirected.current = true;
      openSnackbar({
        open: true,
        message: 'You do not have access to this venue.',
        variant: 'alert',
        alert: { color: 'error' }
      });
      logout();
      return;
    }

    // Has venue access but insufficient role
    const userLevel = ROLE_LEVEL[role] ?? -1;
    const requiredLevel = ROLE_LEVEL[requiredRole] ?? 0;

    if (userLevel < requiredLevel) {
      hasRedirected.current = true;

      openSnackbar({
        open: true,
        message: 'You do not have permission to access this page.',
        variant: 'alert',
        alert: { color: 'error' }
      });
      navigate(`/dashboard/venue/${selectedVenueData.orgID}`, { replace: true });
    }
  }, [user, selectedVenueData, venueDataLoading, requiredRole, role, logout, navigate]);

  // Show loader while venue data is still loading
  if (venueDataLoading || !selectedVenueData || !user) return <Loader />;

  return children;
}

VenueGuard.propTypes = {
  children: PropTypes.node.isRequired,
  requiredRole: PropTypes.oneOf(['staff', 'admin'])
};
